import { useContext, useState } from "react";
import {
	StepAccordion,
	AccordionMinimumHeight,
	AccordionBackgroundColor,
} from "../../../../Utils/StepAccordion";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { styled } from "@mui/material/styles";
import dayjs, { Dayjs } from "dayjs";
import { BoxButton } from "../../../../Utils/UI/BoxButton/BoxButton";
import IStepInterface from "../../../../../Interfaces/IStepInterface";
import "./MovingInStep.scss";
import PartnerContentContext from "../../../../../Contexts/PartnerContentContext";
import { InfoTooltip } from "../../../../Utils/UI/InfoBadge/InfoBadge";
import { Box } from "@mui/material";
import { useOrder } from "../../../../../Contexts/OrderContext";

const MovingInStep = ({
	nextStep,
	previousStep,
	open,
	toggleOpen,
}: IStepInterface) => {
	const { order, updateOrder } = useOrder();
	const [firstButtonSelected, setFirstButtonSelected] = useState(false);
	const [secondButtonSelected, setSecondButtonSelected] = useState(false);
	const [dateValue, setDateValue] = useState<Dayjs | null>(null);
	const [pickerOpen, setPickerOpen] = useState(false);
	const partnerContentContext = useContext(PartnerContentContext);
	const labelShrink = pickerOpen || dateValue !== null;
	
	const getMinDate = (): Dayjs => dayjs().subtract(5, "day");
	const getMaxDate = (): Dayjs => dayjs().add(6, "month");

	function handleDateChange(value: unknown): void {
		const dayjsValue = value as Dayjs | null;
		setDateValue(dayjsValue);
		updateOrder({ startDate: dayjsValue ? dayjsValue.toDate() : undefined });
	}

	return (
		<StepAccordion
			firstButtonId="movingInStepReturn"
			secondButtonId="movingInStepForward"
			minimumHeight={AccordionMinimumHeight.EXTRASMALL}
			firstButtonLabel={
				partnerContentContext.movingInStep.backButton
					? partnerContentContext.movingInStep.backButton
					: "Tilbage"
			}
			firstButton={previousStep}
			firstButtonOutline={true}
			secondButtonLabel={
				partnerContentContext.movingInStep.nextButton
					? partnerContentContext.movingInStep.nextButton
					: "Næste"
			}
			secondButton={firstButtonSelected || secondButtonSelected ? nextStep : undefined}
			secondButtonDisabled={
				(!firstButtonSelected && !secondButtonSelected) || (firstButtonSelected && !order.startDate)
			}
			label={partnerContentContext.movingInStep.title}
			open={open}
			click={toggleOpen}
			backgroundColor={AccordionBackgroundColor.GREY}
		>
			<p>{partnerContentContext.movingInStep.cohabLabel}</p>

			<div className="movingin-wrapper">
				<BoxButton
					height={60}
					label={partnerContentContext.movingInStep.firstBoxButton}
					onClick={() => {
						updateOrder({ cohabCpr: "", cohabFullName: "" });
						setFirstButtonSelected(true);
						setSecondButtonSelected(false);
					}}
					selected={firstButtonSelected}
				/>

				<BoxButton
					height={60}
					label={partnerContentContext.movingInStep.secondBoxButton}
					onClick={() => {
						updateOrder({ startDate: undefined });
						setFirstButtonSelected(false);
						setSecondButtonSelected(true);
					}}
					selected={secondButtonSelected}
				/>
			</div>

			{firstButtonSelected && (
				<Box sx={{ display: "flex", alignItems: "center", marginTop: "20px" }}>
					<StyledDatePicker
						hasValue={dateValue !== null}
						label={
							partnerContentContext.movingInStep.datePlaceholder
								? partnerContentContext.movingInStep.datePlaceholder
								: "Indflytningsdato"
						}
						value={dateValue}
						open={pickerOpen}
						onOpen={() => setPickerOpen(true)}
						onClose={() => setPickerOpen(false)}
						minDate={getMinDate()}
						maxDate={getMaxDate()}
						onChange={handleDateChange}
						format="D.M.YYYY"
						slotProps={{
							textField: {
								inputProps: { readOnly: true },
								InputLabelProps: { shrink: labelShrink },
								onClick: () => setPickerOpen(true),
							},
						}}
					/>
					
					<Box sx={{ ml: 3, mt: 0.5 }}>
						<InfoTooltip placement="right" message="5 dage tilbage / 6 mdr. frem." />
					</Box>
				</Box>
			)}
		</StepAccordion>
	);
};

export default MovingInStep;

const StyledDatePicker = styled(DatePicker, {
	shouldForwardProp: (prop) => prop !== "hasValue",
})<{ hasValue: boolean }>`
	width: 100%;

	${({ theme }) => theme.breakpoints.up("sm")} {
		width: 508px;
	}

	& .MuiInputBase-root {
		font-family: "NORLYSText-Regular", sans-serif;
		font-weight: 300;
		font-size: 16px;
		border-radius: 6px;
		background-color: #fff;
		min-height: 56px;
		padding: 22px 24px 6px 24px;
		display: flex;
		align-items: center;
	}

	& .MuiInputAdornment-root {
		position: absolute;
		right: 14px;
		top: 50%;
		transform: translateY(-50%);
	}

	& .MuiInputBase-input {
		font-family: "NORLYSText-Regular", sans-serif;
		font-weight: 300;
		font-size: 16px;
		padding: 0 !important;
		line-height: 2;
		color: ${({ hasValue }) => (hasValue ? "#323232" : "transparent")};
	}

	& .MuiInputLabel-root {
		font-family: "NORLYSText-Regular", sans-serif;
		font-weight: 300;
		color: #838383;
		transform: translate(24px, 16px) scale(1);
	}

	& .MuiInputLabel-root.Mui-focused,
	& .MuiInputLabel-shrink {
		transform: translate(26px, -5px) scale(0.6);
		transform-origin: left bottom;
		background-color: #fff;
		padding: 0 4px;
		color: #838383;
	}

	& .MuiOutlinedInput-notchedOutline legend {
		display: none;
	}

	& .MuiOutlinedInput-notchedOutline {
		border: 2px solid #d6d6d6;
		border-radius: 6px;
		top: 0;
	}

	& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline {
		border-color: #d6d6d6;
	}

	& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline {
		border-color: #25d287;
		border-width: 2px;
	}
`;
